import type { components, paths } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type WorkflowDefinition = components['schemas']['WorkflowDefinitionResource']
export type WorkflowInput = components['schemas']['WorkflowDefinitionStoreRequest']
type WorkflowMembership = components['schemas']['MembershipResource']
type WorkflowCustomRole = components['schemas']['RoleResource']
export type WorkflowDocumentTypesResponse = NonNullable<
  paths['/workflow/document-types']['get']['responses'][200]['content']['application/json']
>
export type WorkflowDocumentType = WorkflowDocumentTypesResponse['data'][number]

const companyHeaders = (companyId: string) => ({ 'X-Company-Id': companyId })

export function useWorkflowApproverMembers(companyId: string) {
  return useQuery({
    queryKey: ['companies', companyId, 'members'] as const,
    queryFn: async (): Promise<WorkflowMembership[]> => {
      const { data } = await api.GET('/companies/{company}/members', {
        params: { path: { company: companyId } },
        headers: companyHeaders(companyId),
      })
      if (!data) throw new Error('GET /companies/{company}/members returned no body')
      return data.data
    },
  })
}

export function useWorkflowApproverRoles(companyId: string) {
  return useQuery({
    queryKey: ['companies', companyId, 'workflow-custom-roles'] as const,
    queryFn: async (): Promise<WorkflowCustomRole[]> => {
      const { data } = await api.GET('/companies/{company}/roles', {
        params: { path: { company: companyId } },
        headers: companyHeaders(companyId),
      })
      if (!data) throw new Error('GET /companies/{company}/roles returned no body')
      return data.data
    },
  })
}

/** `module` narrows the list to one module's paths (its settings screen, ADR-0034). */
export function useWorkflowDefinitions(companyId: string | null, module?: string) {
  return useQuery({
    queryKey: ['companies', companyId, 'workflows', module ?? 'all'] as const,
    enabled: Boolean(companyId),
    queryFn: async (): Promise<WorkflowDefinition[]> => {
      if (!companyId) return []
      const { data } = await api.GET('/companies/{company}/workflows', {
        params: { path: { company: companyId }, query: module ? { module } : {} },
        headers: companyHeaders(companyId),
      })
      if (!data) throw new Error('Workflow definitions response was empty')
      return data.data
    },
  })
}

export function useWorkflowDocumentTypes(companyId: string | null, module?: string) {
  return useQuery({
    queryKey: ['companies', companyId, 'workflow-document-types', module ?? 'all'] as const,
    enabled: Boolean(companyId),
    queryFn: async (): Promise<WorkflowDocumentTypesResponse> => {
      if (!companyId) throw new Error('A company is required')
      const { data } = await api.GET('/workflow/document-types', {
        params: { query: module ? { module } : {} },
        headers: companyHeaders(companyId),
      })
      if (!data) throw new Error('Workflow document types response was empty')
      return data
    },
  })
}

export function useSaveWorkflow(companyId: string, definitionId?: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: WorkflowInput) => {
      const result = definitionId
        ? await api.PATCH('/companies/{company}/workflows/{definition}', {
            params: { path: { company: companyId, definition: definitionId } },
            headers: companyHeaders(companyId),
            body: input,
          })
        : await api.POST('/companies/{company}/workflows', {
            params: { path: { company: companyId } },
            headers: companyHeaders(companyId),
            body: input,
          })
      if (!result.data) throw new Error('Workflow definition response was empty')
      return result.data.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'workflows'] })
    },
  })
}

export function useDeleteWorkflow(companyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (definitionId: string) => {
      await api.DELETE('/companies/{company}/workflows/{definition}', {
        params: { path: { company: companyId, definition: definitionId } },
        headers: companyHeaders(companyId),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'workflows'] })
    },
  })
}
